import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useStory } from '../contexts/StoryContext.tsx';
import { useVideoStatusQuery, VideoStatusItem } from '../hooks/useVideoStatusQuery.ts';

interface VideoScene {
  scene_number: number;
  video_url: string | null;
  audio_url: string | null;
  prompt: string;
  status: 'generating' | 'completed';
  audio_status: 'generating' | 'completed';
  showDescription?: boolean;
}

// decide how many slots to render
const computeSlotCount = (
  statusCount?: number,
  haveScenes = 0,
  haveMedia = 0,
  currentStep?: string
) => {
  if (typeof statusCount === 'number' && statusCount > 0) return statusCount;
  const derived = Math.max(haveScenes, haveMedia);
  if (derived > 0) return derived;
  return currentStep === 'videos' ? 1 : 0;
};

const VideoBuilder: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedStoryId, storyStatus } = useStory();

  const [videoScenes, setVideoScenes] = useState<VideoScene[]>([]);
  const [loading, setLoading] = useState(true); // page-level hydration spinner
  const [error, setError] = useState<string | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<VideoScene | null>(null);

  // modal media refs
  const [modalVideoRef, setModalVideoRef] = useState<HTMLVideoElement | null>(null);
  const [modalAudioRef, setModalAudioRef] = useState<HTMLAudioElement | null>(null);
  const [cardVideoRefs, setCardVideoRefs] = useState<Map<number, HTMLVideoElement>>(new Map());

  // react-query: video status
  const videoStatusQuery = useVideoStatusQuery(selectedStoryId, storyStatus || undefined);
  const videoStatus: VideoStatusItem[] = (videoStatusQuery.data as VideoStatusItem[]) || [];
  const vsLoading = videoStatusQuery.isLoading;
  const vsError = videoStatusQuery.error;
  
  // Debug query state (remove in production)
  if (process.env.NODE_ENV !== 'production') {
    console.log('VideoBuilder query state:', {
      selectedStoryId,
      queryData: videoStatusQuery.data,
      isLoading: videoStatusQuery.isLoading,
      isFetching: videoStatusQuery.isFetching,
      error: videoStatusQuery.error,
      status: videoStatusQuery.status
    });
  }

  // scroll-to-top on story change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [selectedStoryId]);

  // register/unregister card refs
  const handleVideoRef = useCallback((sceneNumber: number, ref: HTMLVideoElement | null) => {
    setCardVideoRefs((prev) => {
      const next = new Map(prev);
      if (ref) next.set(sceneNumber, ref);
      else next.delete(sceneNumber);
      return next;
    });
  }, []);

  // pause all card videos when modal opens
  useEffect(() => {
    if (!selectedVideo) return;
    cardVideoRefs.forEach((v) => v && !v.paused && v.pause());
  }, [selectedVideo, cardVideoRefs]);

  // pause modal video when modal closes
  useEffect(() => {
    if (!selectedVideo && modalVideoRef && !modalVideoRef.paused) {
      modalVideoRef.pause();
    }
  }, [selectedVideo, modalVideoRef]);

  // modal A/V sync
  const handleModalVideoPlay = () => {
    if (modalAudioRef && selectedVideo?.audio_url) {
      modalAudioRef.currentTime = modalVideoRef?.currentTime || 0;
      modalAudioRef.play().catch(() => {});
    }
  };
  const handleModalVideoPause = () => modalAudioRef?.pause();
  const handleModalVideoSeek = () => {
    if (modalAudioRef && modalVideoRef) modalAudioRef.currentTime = modalVideoRef.currentTime;
  };

  // build local forward-only state from server videoStatus
  useEffect(() => {
    if (!selectedStoryId) return;
    
    if (videoStatus.length === 0) {
      // If no data from server, clear local state
      setVideoScenes([]);
      return;
    }
    
    // Build new scenes directly from videoStatus
    const newScenes: VideoScene[] = videoStatus.map((vs) => ({
      scene_number: vs.scene_number,
      video_url: vs.video_url ?? null,
      audio_url: vs.audio_url ?? null,
      prompt: vs.prompt ?? '',
      status: vs.status,
      audio_status: vs.audio_status,
    }));
    
    const sorted = newScenes.sort((a, b) => a.scene_number - b.scene_number);
    setVideoScenes(sorted);
  }, [selectedStoryId, videoStatus]);

  // manage loading state - stop spinner when we have data OR when query is done loading
  useEffect(() => {
    if (!selectedStoryId) {
      setLoading(false);
      return;
    }
    
    // Set loading to true initially when story changes
    if (videoStatus.length === 0 && vsLoading) {
      setLoading(true);
      return;
    }
    
    // Stop loading if we have data OR if the query is not loading anymore
    const shouldStopLoading = videoStatus.length > 0 || !vsLoading;
    
    if (shouldStopLoading) {
      setLoading(false);
    }
  }, [selectedStoryId, videoStatus.length, vsLoading, storyStatus?.current_step]);

  // reset on story change
  useEffect(() => {
    if (!selectedStoryId) {
      setSelectedVideo(null);
      setError(null);
      setLoading(false);
      return;
    }
    
    // Reset state when story changes (but don't clear videoScenes - let the state building effect handle it)
    setSelectedVideo(null);
    setError(null);
    
    // Force refresh the query to get fresh URLs
    queryClient.invalidateQueries({ queryKey: ['video-status', selectedStoryId] });
  }, [selectedStoryId, queryClient]);

  // early: no story
  if (!selectedStoryId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Story Selected</h2>
          <p className="text-gray-600 mb-4">Please select a story to view its videos.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
          >
            Go to Stories
          </button>
        </div>
      </div>
    );
  }

  // page hydration spinner
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
        </div>
      </div>
    );
  }

  // react-query error
  if (vsError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Videos</h2>
          <p className="text-gray-600 mb-4">{vsError.message}</p>
          <button
            onClick={() => {
              setVideoScenes([]);
              setSelectedVideo(null);
              setError(null);
              setLoading(true);
            }}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // how many cards (placeholders fill gaps)
  const totalSlots = computeSlotCount(
    storyStatus?.shots_count,
    videoScenes.length,
    videoStatus.length,
    storyStatus?.current_step
  );
  
  // Debug grid rendering (remove in production)
  if (process.env.NODE_ENV !== 'production') {
    console.log('VideoBuilder: Grid rendering', {
      totalSlots,
      videoScenesLength: videoScenes.length,
      videoScenes: videoScenes,
      videoStatusLength: videoStatus.length
    });
  }

  if (totalSlots === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="text-6xl mb-6">🎥</div>
          <h2 className="text-3xl font-bold text-gray-800 mb-4">No Videos Yet</h2>
          <p className="text-gray-600">
            We’ll show your scenes here as soon as video generation starts.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100">
      <div className="container mx-auto px-6 py-8">
        {/* grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: totalSlots }).map((_, i) => {
            const scene = videoScenes.find((s) => s.scene_number === i + 1);

            return scene ? (
              <VideoCard
                key={scene.scene_number}
                scene={scene}
                onSelectVideo={setSelectedVideo}
                onVideoRef={handleVideoRef}
                setSelectedVideo={setSelectedVideo}
              />
            ) : (
              <div
                key={`placeholder-${i}`}
                className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
              >
                <div className="aspect-video bg-gray-100 flex items-center justify-center relative">
                  <div className="flex flex-col items-center justify-center text-gray-400">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mb-2"></div>
                    <div className="text-sm">Generating Video...</div>
                    <div className="text-xs text-gray-400 mt-1">Click to view details</div>
                  </div>
                  <div className="absolute top-2 right-2 flex flex-col space-y-1">
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 animate-pulse">
                      Video
                    </span>
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 animate-pulse">
                      Audio
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-gray-800">Scene {i + 1}</h3>
                  <div className="mt-3">
                    <div className="bg-gray-200 rounded-full h-1 overflow-hidden">
                      <div className="bg-gradient-to-r from-purple-600 to-pink-600 h-1 rounded-full w-full animate-pulse"></div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Video Modal */}
      {selectedVideo && !selectedVideo.showDescription && (
        <div
          className="fixed inset-0 lg:left-64 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedVideo(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-4xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-semibold text-gray-900">Scene {selectedVideo.scene_number}</h2>
              <button
                onClick={() => setSelectedVideo(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close modal"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4">
              <div className="mb-4">
                {selectedVideo.video_url ? (
                  <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                    <video
                      ref={setModalVideoRef}
                      src={selectedVideo.video_url}
                      className="w-full h-full object-cover"
                      onError={() => {
                        if (selectedStoryId) {
                          // refresh to get a fresh pre-signed URL
                          queryClient.invalidateQueries({ queryKey: ['video-status', selectedStoryId] });
                        }
                      }}
                      onPlay={handleModalVideoPlay}
                      onPause={handleModalVideoPause}
                      onSeeked={handleModalVideoSeek}
                      onClick={(e) => e.stopPropagation()}
                      controls
                    />
                  </div>
                ) : (
                  <div className="aspect-video bg-gray-200 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                      <p className="text-gray-600 font-medium">Generating Video...</p>
                      <p className="text-sm text-gray-500 mt-2">This video is being processed</p>
                    </div>
                  </div>
                )}
              </div>

              {selectedVideo.audio_url && (
                <audio ref={setModalAudioRef} src={selectedVideo.audio_url} style={{ display: 'none' }} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Description Modal */}
      {selectedVideo && selectedVideo.showDescription && (
        <div
          className="fixed inset-0 lg:left-64 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedVideo(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Scene {selectedVideo.scene_number} - Description
              </h2>
              <button
                onClick={() => setSelectedVideo(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              <div className="prose max-w-none">
                <p className="text-gray-700 leading-relaxed">
                  {selectedVideo.prompt || 'No description available for this scene.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface VideoCardProps {
  scene: VideoScene;
  onSelectVideo: (scene: VideoScene) => void;
  onVideoRef: (sceneNumber: number, ref: HTMLVideoElement | null) => void;
  setSelectedVideo: (scene: VideoScene | null) => void;
}

const VideoCard: React.FC<VideoCardProps> = ({ scene, onSelectVideo, onVideoRef, setSelectedVideo }) => {
  const [videoError, setVideoError] = useState(false);
  const [videoRef, setVideoRef] = useState<HTMLVideoElement | null>(null);
  const [audioRef, setAudioRef] = useState<HTMLAudioElement | null>(null);

  const queryClient = useQueryClient();
  const { selectedStoryId } = useStory();

  const isVideoGenerating = !scene.video_url || scene.status === 'generating';
  const isAudioGenerating = !scene.audio_url || scene.audio_status === 'generating';

  useEffect(() => {
    onVideoRef(scene.scene_number, videoRef);
    return () => onVideoRef(scene.scene_number, null);
  }, [videoRef, scene.scene_number, onVideoRef]);

  const handleVideoPlay = () => {
    if (audioRef && scene.audio_url) {
      audioRef.currentTime = videoRef?.currentTime || 0;
      audioRef.play().catch(() => {});
    }
  };
  const handleVideoPause = () => audioRef?.pause();
  const handleVideoSeek = () => {
    if (audioRef && videoRef) audioRef.currentTime = videoRef.currentTime;
  };

  return (
    <div
      className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 border border-gray-200 cursor-pointer"
      onClick={() => onSelectVideo(scene)}
    >
      <div className="aspect-video mb-4 rounded-t-xl overflow-hidden bg-gray-100 flex items-center justify-center relative">
        {scene.video_url && !videoError ? (
          <video
            ref={setVideoRef}
            src={scene.video_url}
            className="w-full h-full object-cover"
            onError={() => {
              setVideoError(true);
              if (selectedStoryId) {
                // refresh to get a fresh pre-signed URL
                queryClient.invalidateQueries({ queryKey: ['video-status', selectedStoryId] });
              }
            }}
            onPlay={handleVideoPlay}
            onPause={handleVideoPause}
            onSeeked={handleVideoSeek}
            onClick={(e) => e.stopPropagation()}
            controls
          />
        ) : (
          <div className="flex flex-col items-center justify-center text-gray-400">
            {isVideoGenerating ? (
              <>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mb-2"></div>
                <div className="text-sm">Generating Video...</div>
                <div className="text-xs text-gray-400 mt-1">Click to view details</div>
              </>
            ) : (
              <div className="text-4xl">🎥</div>
            )}
          </div>
        )}

        <div className="absolute top-2 right-2 flex flex-col space-y-1">
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              isVideoGenerating ? 'bg-yellow-100 text-yellow-800 animate-pulse' : 'bg-green-100 text-green-800'
            }`}
          >
            {isVideoGenerating ? 'Video' : 'Video ✓'}
          </span>
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              isAudioGenerating ? 'bg-yellow-100 text-yellow-800 animate-pulse' : 'bg-green-100 text-green-800'
            }`}
          >
            {isAudioGenerating ? 'Audio' : 'Audio ✓'}
          </span>
        </div>
      </div>

      <div className="p-4">
        <h3 className="font-semibold text-gray-800 mb-2">Scene {scene.scene_number}</h3>

        <button
          onClick={(e) => {
            e.stopPropagation();
            setSelectedVideo({ ...scene, showDescription: true });
          }}
          className="w-full px-3 py-2 text-sm font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors duration-200 mb-4"
        >
          Description
        </button>

        {scene.audio_url && <audio ref={setAudioRef} src={scene.audio_url} style={{ display: 'none' }} />}
      </div>
    </div>
  );
};

export default VideoBuilder;
